import React from 'react';
import {useLocation} from 'react-router-dom';
import api from '../api';
import useMixPanel from './useMixPanel';
import {
  Statuses,
  useAbortController,
  useErrorModal,
  useIsMounted,
  useStatus,
} from '../utils/hooks';
import {useComputedReservationDetails} from '../context/computedReservationDetails';
import {LocationStates} from '../views/VerifyModalScreen/VerifyModalScreen';
import {GlobalLocationTypes} from '../utils/types';
import {OCR_CHECKING_STATUSES} from '../utils/constants';
import scanningErrorIcon from '../assets/scanning_doc_error.svg';

type StartDocumentFaceDetection = {
  image: string;
  reservationId: string;
  onSuccess: (image: string, checkId: string) => void;
  onError?: (error?: string, aborted?: boolean) => void;
};

type StartComparingFaceDetection = {selfieCheckId: string} & Omit<
  StartDocumentFaceDetection,
  'reservationId'
>;

type CheckCompareProps = {
  id: string;
  image: string;
  selfieCheckId: string;
};

function useDocumentSelfieDetection() {
  const isMounted = useIsMounted();
  const location = useLocation<GlobalLocationTypes>();
  const onSuccessRef = React.useRef<((image: string, data: any) => void) | null>(null);
  const onErrorRef = React.useRef<
    ((error?: string, aborted?: boolean) => void) | undefined
  >(undefined);
  const statusTimeoutRef = React.useRef<any>();
  const {abortController, setupAbortController} = useAbortController();
  const {status, setStatus} = useStatus();
  const [error, setError] = React.useState<string | null | undefined>(null);
  const {ErrorModal, displayError} = useErrorModal();
  const reservationDetails = useComputedReservationDetails();
  const {IdVerificationDocument} = useMixPanel();
  const abortCurrentRequest = React.useCallback(() => {
    abortController.abort();
  }, [abortController]);

  React.useEffect(() => {
    return () => {
      abortController.abort();
      clearTimeout(statusTimeoutRef.current);
    };
  }, [abortController]);

  const resetDetection = React.useCallback(() => {
    setStatus(Statuses.idle);
    setError(null);
    abortCurrentRequest();
    onSuccessRef.current = null;
  }, [abortCurrentRequest, setStatus]);

  const handleError = React.useCallback(
    (error?: string, aborted?: boolean) => {
      if (!aborted) {
        onErrorRef.current?.(error, aborted);
        displayError(error);
        setError(error);
        setStatus(Statuses.error);
        statusTimeoutRef.current = setTimeout(() => {
          setError(null);
          setStatus(Statuses.idle);
        }, 3000);
      }
    },
    [displayError, setStatus],
  );

  const checkDocumentFaceDetection = React.useCallback(
    async (documentCheckId: string, image: string) => {
      const signal = setupAbortController();
      const {data, error, aborted} = await api.ocr.checkDocumentFaceDetection(
        documentCheckId,
        {
          signal,
        },
      );
      const isPhotoVerificationDisabled = Boolean(
        reservationDetails.reservation?.is_biomatch_photo_verification_disabled,
      );

      const isInProgress =
        !isPhotoVerificationDisabled && data?.status === OCR_CHECKING_STATUSES.processing;
      const isSuccess =
        isPhotoVerificationDisabled ||
        (data?.status === OCR_CHECKING_STATUSES.complete && data?.is_face_detected);
      const isError =
        data?.status === OCR_CHECKING_STATUSES.error || !data?.is_face_detected;

      if (!isMounted.current) {
        return;
      }

      if (error) {
        IdVerificationDocument('Error');
        handleError(error, aborted);
        return;
      }

      if (isInProgress) {
        await checkDocumentFaceDetection(documentCheckId, image);
        return;
      }

      if (isSuccess) {
        IdVerificationDocument('Success');
        setStatus(Statuses.success);
        onSuccessRef.current?.(image, data);
        return;
      }

      if (isError) {
        IdVerificationDocument('Error');
        handleError();
      }
    },
    [
      IdVerificationDocument,
      handleError,
      isMounted,
      reservationDetails.reservation?.is_biomatch_photo_verification_disabled,
      setStatus,
      setupAbortController,
    ],
  );

  const startDocumentFaceDetection = React.useCallback(
    async ({image = '', reservationId = '', onSuccess}: StartDocumentFaceDetection) => {
      setStatus(Statuses.loading);
      onSuccessRef.current = onSuccess;
      const signal = setupAbortController();
      const {data, error, aborted} = await api.ocr.sendDocumentForFaceDetection(
        image,
        reservationId,
        {
          signal,
        },
      );

      if (!isMounted.current) {
        return;
      }
      if (data) {
        await checkDocumentFaceDetection(data.id, image);
      }
      if (error) {
        IdVerificationDocument('Error');
        handleError(error, aborted);
      }
    },
    [
      IdVerificationDocument,
      checkDocumentFaceDetection,
      handleError,
      isMounted,
      setStatus,
      setupAbortController,
    ],
  );

  const checkSelfieAndDocumentFacesComparing = React.useCallback(
    async ({id, image, selfieCheckId}: CheckCompareProps) => {
      const signal = setupAbortController();
      const {data, error, aborted} =
        await api.ocr.checkSelfieAndDocumentFacesComparingStatus(id, {
          signal,
        });
      const isPhotoVerificationDisabled = Boolean(
        reservationDetails.reservation?.is_biomatch_photo_verification_disabled,
      );
      const isInProgress =
        !isPhotoVerificationDisabled && data?.status === OCR_CHECKING_STATUSES.processing;
      const isComplete =
        isPhotoVerificationDisabled || data?.status === OCR_CHECKING_STATUSES.complete;
      const isError = data?.status === OCR_CHECKING_STATUSES.error;

      if (!isMounted.current) {
        return;
      }

      if (error) {
        handleError(error, aborted);
        return;
      }

      if (isInProgress) {
        await checkSelfieAndDocumentFacesComparing({id, image, selfieCheckId});
        return;
      }

      if (isComplete) {
        const resultData = {...data, selfieCheckId};
        onSuccessRef.current?.(image, resultData);
        return;
      }

      if (isError) {
        handleError();
      }
    },
    [
      handleError,
      isMounted,
      reservationDetails.reservation?.is_biomatch_photo_verification_disabled,
      setupAbortController,
    ],
  );

  const startSelfieAndDocumentFacesComparing = React.useCallback(
    async ({
      selfieCheckId = '',
      image,
      onSuccess,
      onError,
    }: StartComparingFaceDetection) => {
      onSuccessRef.current = onSuccess;
      onErrorRef.current = onError;

      const signal = setupAbortController();

      const {data, error, aborted} = await api.ocr.compareSelfieAndDocumentFaces({
        signal,
        selfieCheckId,
        documentCheckId:
          location.state?.[LocationStates.frontSideDocumentPhoto]?.checkId!,
        reservationId: reservationDetails?.reservation?.id,
      });

      if (!isMounted.current) {
        return;
      }

      if (data) {
        await checkSelfieAndDocumentFacesComparing({id: data?.id, image, selfieCheckId});
      }
      if (error) {
        handleError(error, aborted);
      }
    },
    [
      checkSelfieAndDocumentFacesComparing,
      handleError,
      isMounted,
      location.state,
      reservationDetails?.reservation?.id,
      setupAbortController,
    ],
  );

  const checkSelfieFaceDetection = React.useCallback(
    async (selfieCheckId: string, image: string) => {
      const signal = setupAbortController();
      const {data, error, aborted} = await api.ocr.checkSelfieFaceDetection(
        selfieCheckId,
        {
          signal,
        },
      );

      const isPhotoVerificationDisabled = Boolean(
        reservationDetails.reservation?.is_biomatch_photo_verification_disabled,
      );
      const isInProgress =
        !isPhotoVerificationDisabled && data?.status === OCR_CHECKING_STATUSES.processing;
      const isComplete =
        isPhotoVerificationDisabled ||
        (data?.status === OCR_CHECKING_STATUSES.complete && data?.is_face_detected);
      const isError =
        data.status === OCR_CHECKING_STATUSES.error || data?.is_face_detected === false;

      if (!isMounted.current) {
        return;
      }

      if (error) {
        handleError(error, aborted);
        return;
      }

      if (isInProgress) {
        await checkSelfieFaceDetection(selfieCheckId, image);
        return;
      }

      if (isComplete) {
        setStatus(Statuses.success);
        onSuccessRef.current?.(image, data);
        return;
      }

      if (isError) {
        handleError();
      }
    },
    [
      handleError,
      isMounted,
      reservationDetails.reservation?.is_biomatch_photo_verification_disabled,
      setStatus,
      setupAbortController,
    ],
  );

  const startSelfieFaceDetection = React.useCallback(
    async ({
      image = '',
      reservationId = '',
      onSuccess,
      onError,
    }: StartDocumentFaceDetection) => {
      setStatus(Statuses.loading);
      onSuccessRef.current = onSuccess;
      onErrorRef.current = onError;

      const signal = setupAbortController();
      const {data, error, aborted} = await api.ocr.sendSelfieForFaceDetection(
        image,
        reservationId,
        {
          signal,
        },
      );

      if (!isMounted.current) {
        return;
      }

      if (error) {
        handleError(error, aborted);
        return;
      }
      if (data) {
        await checkSelfieFaceDetection(data.id, image);
      }
    },
    [checkSelfieFaceDetection, handleError, isMounted, setStatus, setupAbortController],
  );

  const DetectionErrorModal = React.useCallback(() => {
    return <ErrorModal iconSrc={scanningErrorIcon} />;
  }, [ErrorModal]);

  return {
    startDocumentFaceDetection,
    startSelfieFaceDetection,
    startSelfieAndDocumentFacesComparing,
    abortCurrentRequest,
    resetDetection,
    status,
    error,
    DetectionErrorModal,
  };
}

export {useDocumentSelfieDetection};
